import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton from 'react-loading-skeleton'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavItem,
    NavLink,
    UncontrolledDropdown,
} from 'reactstrap'
import FilterIcon from 'assets/icons/FilterIcon'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { AppDispatch, RootState } from 'store'
import {
    Contact,
    CreatorContactsFilters,
    CreatorsListTab,
    CreatorSortOptionLabel,
    CreatorSortOptions,
    defaultCreatorContactsFilters,
} from 'types/creators'
import { copyToClipboard, formatNumber, generateUniqueKey, getSocialAccountProfileURL } from 'utils/helpers'
import useDocumentTitle from 'hooks/useDocumentTitle'
import userDummyImg from 'assets/images/users/user-dummy-img.jpg'
import { showCampaignInviteModal } from 'slices/ui/reducer'
import { resetCreatorsState, updateCreatorsFilters } from 'slices/creators/reducer'
import CreatorFiltersModal from 'Components/creators/CreatorFiltersModal'
import BulkUploadCreatorContactModal from 'Components/creators/BulkUploadCreatorContactModal'
import AddCreatorContactModal from 'Components/creators/AddCreatorContactModal'
import AddContactMobileModal from 'Components/creators/AddContactMobileModal'
import HandleCreatorTagsModal from 'Components/creators/HandleCreatorTagsModal'
import ManageCreatorTagsOffcanvas from 'Components/creators/ManageCreatorTagsOffcanvas'
import eventEmitter from 'utils/eventEmitter'
import './creators.scss'

const SEARCH_DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 3
const BULK_POLL_INTERVAL_MS = 5000

const getFiltersForTab = (
    tab: CreatorsListTab,
    baseFilters: CreatorContactsFilters
): CreatorContactsFilters => {
    const next: CreatorContactsFilters = { ...baseFilters, page: 1 }
    delete next.unprocessed
    delete next.has_error
    if (tab === 'unprocessed') next.unprocessed = 'y'
    if (tab === 'failed') next.has_error = 'y'
    return next
}

const stripTabFilters = (filters: CreatorContactsFilters): CreatorContactsFilters => {
    const next = { ...filters }
    delete next.unprocessed
    delete next.has_error
    return next
}

const platformLabel = (platform?: string) => {
    if (platform === 'ig') return 'Instagram'
    if (platform === 'yt') return 'YouTube'
    return platform ?? '—'
}

const formatHandle = (handle?: string) => {
    if (!handle) return '—'
    return handle.startsWith('@') ? handle : `@${handle}`
}

const CreatorContactRow: React.FC<{
    contact: Contact
    isSelected: boolean
    onToggleSelect: (socialAccountId: string) => void
    onEditTags: (contact: Contact) => void
    onAddMobile: (contact: Contact) => void
}> = ({ contact, isSelected, onToggleSelect, onEditTags, onAddMobile }) => {
    const socialAccount = contact.social_account
    const platform = socialAccount?.platform ?? contact.platform
    const mobile = contact.mobile
    const avatar = socialAccount?.profile_picture_url?.split('=')[0] || userDummyImg
    const name = socialAccount?.name ?? contact.influencer?.name ?? '—'
    const handle = socialAccount?.handle ?? contact.handle
    const socialAccountId = socialAccount?.uuid
    const hasMobile = Boolean(mobile)
    const canSelect = Boolean(hasMobile && socialAccountId)

    const handleSelectChange = () => {
        if (!socialAccountId) return
        if (!hasMobile) {
            onAddMobile(contact)
            return
        }
        onToggleSelect(socialAccountId)
    }

    return (
        <article className={`creators-row${isSelected ? ' creators-row--selected' : ''}`}>
            <div className="creators-row__select">
                <input
                    type="checkbox"
                    className="form-check-input creators-row__checkbox"
                    checked={canSelect && isSelected}
                    disabled={!socialAccountId}
                    onChange={handleSelectChange}
                    aria-label={hasMobile ? `Select ${name}` : `Add mobile to select ${name}`}
                    title={!hasMobile ? 'Add a mobile number to invite this creator' : undefined}
                />
            </div>
            <div className="creators-row__profile">
                <div className="creators-row__avatar-wrap">
                    <img
                        src={avatar}
                        alt={name}
                        className="creators-row__avatar"
                        onError={(e) => {
                            e.currentTarget.src = userDummyImg
                        }}
                    />
                    {platform === 'ig' && (
                        <span className="creators-row__platform-badge">
                            <i className="ri-instagram-line" />
                        </span>
                    )}
                    {platform === 'yt' && (
                        <span className="creators-row__platform-badge creators-row__platform-badge--yt">
                            <i className="ri-youtube-fill" />
                        </span>
                    )}
                </div>
                <div className="creators-row__info">
                    <span className="creators-row__name">{name}</span>
                    <div className="creators-row__handle-row">
                        <span className="creators-row__handle">{formatHandle(handle)}</span>
                        {handle && (
                            <a
                                href={getSocialAccountProfileURL(handle, platform)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="creators-row__external"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <i className="ri-external-link-line" />
                            </a>
                        )}
                    </div>
                </div>
            </div>
            <div className="creators-row__metrics">
                <div className="creators-row__metric">
                    <span className="creators-row__metric-label">Followers</span>
                    <span className="creators-row__metric-value">
                        {formatNumber(socialAccount?.followers_count ?? 0)}
                    </span>
                </div>
                <div className="creators-row__metric">
                    <span className="creators-row__metric-label">Avg Views</span>
                    <span className="creators-row__metric-value">
                        {formatNumber(socialAccount?.average_views ?? 0)}
                    </span>
                </div>
                <div className="creators-row__metric">
                    <span className="creators-row__metric-label">Engg Score</span>
                    <span className="creators-row__metric-value">
                        {socialAccount?.engagement_score ?? '—'}
                    </span>
                </div>
            </div>
            <div className="creators-row__metric creators-row__mobile">
                <span className="creators-row__metric-label">Mobile</span>
                {mobile ? (
                    <span className="creators-row__metric-value creators-row__metric-value--mobile">
                        {mobile}
                        <button
                            type="button"
                            className="creators-row__icon-btn"
                            onClick={() => copyToClipboard(mobile, 'Mobile number copied to your clipboard!')}
                            aria-label="Copy mobile"
                        >
                            <i className="ri-file-copy-line" />
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        className="creators-row__link-btn"
                        onClick={() => onAddMobile(contact)}
                    >
                        Add Mobile
                    </button>
                )}
            </div>
            <div className="creators-row__tags">
                <span className="creators-row__metric-label">Tags</span>
                <div className="creators-row__tags-value">
                    {(contact.tags ?? []).slice(0, 3).map((tag) => (
                        <span key={tag} className="creators-row__tag">
                            {tag}
                        </span>
                    ))}
                    {(contact.tags?.length ?? 0) > 3 && (
                        <span className="creators-row__tag">+{contact.tags.length - 3}</span>
                    )}
                    <button
                        type="button"
                        className="creators-row__icon-btn"
                        onClick={() => onEditTags(contact)}
                        aria-label="Edit creator tags"
                    >
                        <i className="ri-pencil-line" />
                    </button>
                </div>
            </div>
        </article>
    )
}

const BulkStatusRow: React.FC<{ contact: Contact; showError?: boolean }> = ({ contact, showError }) => {
    const platform = contact.social_account?.platform ?? contact.platform
    const handle = contact.social_account?.handle ?? contact.handle

    return (
        <article className={`creators-status-row${showError ? ' creators-status-row--failed' : ''}`}>
            <div className="creators-status-row__identity">
                <span className="creators-row__name">{formatHandle(handle)}</span>
                <span className="creators-row__handle">{platformLabel(platform)}</span>
            </div>
            <div className="creators-status-row__detail">
                {showError ? (
                    <p className="creators-status-row__error">{contact.error ?? 'Unknown error'}</p>
                ) : contact.tags?.length ? (
                    <div className="creators-row__tags">
                        {contact.tags.map((tag) => (
                            <span key={tag} className="creators-row__tag">
                                {tag}
                            </span>
                        ))}
                    </div>
                ) : null}
            </div>
            <span className="creators-status-row__date">{contact.created}</span>
        </article>
    )
}

const Creators = () => {
    useDocumentTitle('Creators')
    const dispatch: AppDispatch = useDispatch()
    const [activeTab, setActiveTab] = useState<CreatorsListTab>('all')
    const [baseFilters, setBaseFilters] = useState(defaultCreatorContactsFilters)
    const filters = useMemo(() => getFiltersForTab(activeTab, baseFilters), [activeTab, baseFilters])
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']))
    const [searchInput, setSearchInput] = useState(baseFilters.q ?? '')
    const [filtersModalOpen, setFiltersModalOpen] = useState(false)
    const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false)
    const [addCreatorModalOpen, setAddCreatorModalOpen] = useState(false)
    const [manageTagsOpen, setManageTagsOpen] = useState(false)
    const [editTagsState, setEditTagsState] = useState<{
        socialAccountId: string
        tags: string[]
        creatorName?: string
    } | null>(null)
    const [addMobileState, setAddMobileState] = useState<{
        socialAccountId: string
        creatorName?: string
    } | null>(null)
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([])
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const creatorsData = useSelector((state: RootState) => state.creators.filtered_lists[filterId])
    const creators = creatorsData?.list ?? []
    const allTotal = useSelector((state: RootState) => state.creators.total)
    const page = creatorsData?.page ?? 1
    const has_more = creatorsData?.has_more ?? true
    const unprocessed = useSelector((state: RootState) => state.creators.unprocessed)
    const failed = useSelector((state: RootState) => state.creators.failed)
    const loading = useSelector((state: RootState) => state.creators.loading)
    const [initialLoading, setInitialLoading] = useState(true)

    const refreshList = useCallback(
        (nextFilters: CreatorContactsFilters, force = false) =>
            dispatch(fetchContactsThunk({ force, filters: nextFilters })),
        [dispatch]
    )

    useEffect(() => {
        dispatch(updateCreatorsFilters(stripTabFilters(baseFilters)))
    }, [baseFilters, dispatch])

    useEffect(() => {
        setInitialLoading(true)
        refreshList(filters).finally(() => {
            setFilterId(generateUniqueKey(filters, ['page']))
            setInitialLoading(false)
        })
    }, [filters, refreshList])

    useEffect(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }
        if (unprocessed <= 0) return

        const mainFilters = stripTabFilters(baseFilters)
        pollingRef.current = setInterval(() => {
            refreshList(mainFilters, true)
            if (activeTab === 'unprocessed') {
                refreshList(getFiltersForTab('unprocessed', baseFilters), true)
            } else if (activeTab === 'failed') {
                refreshList(getFiltersForTab('failed', baseFilters), true)
            }
        }, BULK_POLL_INTERVAL_MS)

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
            }
        }
    }, [unprocessed, activeTab, baseFilters, refreshList])

    useEffect(() => {
        if (activeTab === 'unprocessed' && unprocessed === 0) setActiveTab('all')
        if (activeTab === 'failed' && failed === 0) setActiveTab('all')
    }, [activeTab, unprocessed, failed])

    useEffect(() => {
        const timeout = setTimeout(() => {
            const trimmed = searchInput.trim()
            const q = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined
            const prevQ = (baseFilters.q ?? '').trim() || undefined
            if (q === prevQ) return

            const next = { ...baseFilters, page: 1 }
            if (q) next.q = q
            else delete next.q
            setBaseFilters(next)
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timeout)
    }, [searchInput, baseFilters])

    useEffect(() => {
        const clearSelection = () => setSelectedCreatorIds([])
        eventEmitter.on('campaign-invite-success', clearSelection)
        return () => {
            eventEmitter.off('campaign-invite-success', clearSelection)
        }
    }, [])

    const nextPage = () => {
        if (has_more) {
            refreshList({ ...filters, page: page + 1 })
        }
    }

    const handleBulkUploadSuccess = () => {
        dispatch(resetCreatorsState())
        refreshList(stripTabFilters(baseFilters), true)
        if (activeTab === 'unprocessed') {
            refreshList(getFiltersForTab('unprocessed', baseFilters), true)
        }
    }

    const handleToggleCreatorSelect = (creatorId: string) => {
        setSelectedCreatorIds((prev) =>
            prev.includes(creatorId) ? prev.filter((id) => id !== creatorId) : [...prev, creatorId]
        )
    }

    const handleInviteToCampaign = () => {
        if (selectedCreatorIds.length === 0) return
        const selectedCreators = creators
            .filter((contact) => selectedCreatorIds.includes(contact.social_account?.uuid))
            .map((contact) => ({
                uuid: contact.social_account.uuid,
                name: contact.social_account?.name ?? contact.influencer?.name ?? contact.handle,
                handle: contact.social_account?.handle ?? contact.handle,
                profile_picture_url: contact.social_account?.profile_picture_url ?? null,
                platform: contact.social_account?.platform ?? contact.platform,
                followers_count: contact.social_account?.followers_count,
            }))
        dispatch(
            showCampaignInviteModal({
                socialAccountIds: selectedCreatorIds,
                creators: selectedCreators,
                mode: 'managed',
            })
        )
    }

    const handleEditTags = (contact: Contact) => {
        if (!contact.social_account?.uuid) return
        setEditTagsState({
            socialAccountId: String(contact.social_account.uuid),
            tags: contact.tags ?? [],
            creatorName: contact.social_account?.name ?? contact.influencer?.name ?? contact.handle,
        })
    }

    const handleAddMobile = (contact: Contact) => {
        setAddMobileState({
            socialAccountId: contact.social_account?.uuid ?? '',
            creatorName: contact.social_account?.name ?? contact.influencer?.name ?? contact.handle,
        })
    }

    const handleAddCreatorSuccess = (contact: Contact) => {
        const socialAccountId = contact?.social_account?.uuid
        if (!socialAccountId) return
        setAddMobileState({
            socialAccountId: String(socialAccountId),
            creatorName: `@${contact.handle}`,
        })
    }

    const showSkeleton = initialLoading || (loading && creators.length === 0)
    const isStatusTab = activeTab === 'unprocessed' || activeTab === 'failed'
    const hasSelection = selectedCreatorIds.length > 0
    const hasAdvancedFilters = Boolean(
        baseFilters.platform ||
            baseFilters.average_followers ||
            baseFilters.followers_count ||
            baseFilters.engagement_score
    )
    const emptyMessage =
        activeTab === 'unprocessed'
            ? 'No creators are currently being processed'
            : activeTab === 'failed'
              ? 'No failed imports'
              : 'Currently there is no creator in your list'

    return (
        <>
            <div className="campaign-page creators-page">
                <h1 className="campaign-page-title">Creators</h1>
                <div className="d-flex justify-content-between align-items-center campaign-page-info flex-column flex-lg-row mt-4 gap-2 gap-lg-0">
                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav w-100 w-lg-50">
                        <NavItem className="campaign-nav-item">
                            <NavLink
                                style={{ cursor: 'pointer' }}
                                className={classnames('campaign-nav-link text-start', {
                                    'active-campaign-nav': activeTab === 'all',
                                })}
                                onClick={() => setActiveTab('all')}
                            >
                                All Creators
                                <span className="count-badge mx-2">{allTotal}</span>
                            </NavLink>
                        </NavItem>
                        {unprocessed > 0 && (
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={classnames('campaign-nav-link text-start', {
                                        'active-campaign-nav': activeTab === 'unprocessed',
                                    })}
                                    onClick={() => setActiveTab('unprocessed')}
                                >
                                    Processing
                                    <span className="count-badge mx-2">{unprocessed}</span>
                                </NavLink>
                            </NavItem>
                        )}
                        {failed > 0 && (
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={classnames('campaign-nav-link text-start', {
                                        'active-campaign-nav': activeTab === 'failed',
                                    })}
                                    onClick={() => setActiveTab('failed')}
                                >
                                    Failed
                                    <span className="count-badge mx-2">{failed}</span>
                                </NavLink>
                            </NavItem>
                        )}
                    </Nav>
                    <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                        {hasSelection && activeTab === 'all' && (
                            <>
                                <span className="creators-page__selected-count">
                                    {selectedCreatorIds.length} selected
                                </span>
                                <button
                                    type="button"
                                    className="creators-page__clear"
                                    onClick={() => setSelectedCreatorIds([])}
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    className="theme-btn theme-btn-md btn-gradient"
                                    onClick={handleInviteToCampaign}
                                >
                                    <i className="ri-mail-send-line me-1" />
                                    Invite to Campaign
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="theme-btn theme-btn-md btn-primary-outlined"
                            onClick={() => setBulkUploadModalOpen(true)}
                        >
                            <i className="ri-upload-2-line me-1" />
                            Bulk Upload
                        </button>
                        <button
                            type="button"
                            className="theme-btn theme-btn-md btn-primary-outlined"
                            onClick={() => setManageTagsOpen(true)}
                        >
                            Manage Tags
                        </button>
                        <button
                            type="button"
                            className="theme-btn theme-btn-md btn-gradient"
                            onClick={() => setAddCreatorModalOpen(true)}
                        >
                            <i className="ri-add-line me-1" />
                            Add Creator
                        </button>
                    </div>
                </div>
                <div className="campaign-page-content my-3">
                    <div className="d-flex justify-content-end align-items-center flex-wrap gap-2 mb-3">
                        <button
                            type="button"
                            className={classnames('btn btn-white d-flex align-items-center', {
                                active: hasAdvancedFilters,
                            })}
                            onClick={() => setFiltersModalOpen(true)}
                        >
                            <FilterIcon size={16} className="me-1" />
                            Filters
                            <i className="mdi mdi-chevron-down fs-5" />
                        </button>
                        <UncontrolledDropdown className="sort-dropdown">
                            <DropdownToggle
                                tag="button"
                                className="btn btn-white d-flex justify-content-between align-items-center w-100"
                            >
                                <span>
                                    <span className="text-muted me-1">Sort by: </span>
                                    {CreatorSortOptionLabel[baseFilters.sort as CreatorSortOptions]}
                                </span>
                                <i className="mdi mdi-chevron-down fs-5" />
                            </DropdownToggle>
                            <DropdownMenu className="mt-1 p-0 w-100">
                                {Object.values(CreatorSortOptions).map((sortOption) => (
                                    <DropdownItem
                                        key={sortOption}
                                        onClick={() =>
                                            setBaseFilters({
                                                ...baseFilters,
                                                sort: sortOption as CreatorSortOptions,
                                                page: 1,
                                            })
                                        }
                                    >
                                        {CreatorSortOptionLabel[sortOption]}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                        <div className="creators-page__search campaign-layout-search-container position-relative">
                            <span>
                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z"
                                        fill="#BBD8ED"
                                        stroke="#0069AD"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="campaign-layout-search-input"
                            />
                            {searchInput && (
                                <span className="search-clear-icon" onClick={() => setSearchInput('')}>
                                    <i className="bx bx-x" />
                                </span>
                            )}
                        </div>
                    </div>

                    {unprocessed > 0 && activeTab === 'all' && (
                        <div className="creators-page__banner">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                            Processing {unprocessed} creator{unprocessed === 1 ? '' : 's'}. This list will update automatically.
                        </div>
                    )}

                    {showSkeleton &&
                        Array.from({ length: 4 }).map((_, index) => (
                            <Skeleton key={index} height={88} className="mb-2 w-100" />
                        ))}
                    {!showSkeleton && creators.length === 0 && (
                        <div className="d-flex w-100 justify-content-center align-items-center">
                            <div className="length-zero">
                                <h3>{emptyMessage}</h3>
                            </div>
                        </div>
                    )}
                    {!showSkeleton && creators.length > 0 && (
                        <InfiniteScroll
                            dataLength={creators.length}
                            next={nextPage}
                            className="overflow-x-hidden"
                            hasMore={has_more}
                            loader={<Skeleton height={88} className="mt-2 w-100" />}
                        >
                            <div className="creators-page__rows">
                                {isStatusTab
                                    ? creators.map((contact) => (
                                          <BulkStatusRow
                                              key={contact.id ?? `${contact.handle}-${contact.platform}`}
                                              contact={contact}
                                              showError={activeTab === 'failed'}
                                          />
                                      ))
                                    : creators.map((contact) => (
                                          <CreatorContactRow
                                              key={
                                                  contact.social_account?.uuid ??
                                                  `${contact.mobile}-${contact.handle}`
                                              }
                                              contact={contact}
                                              isSelected={selectedCreatorIds.includes(
                                                  contact.social_account?.uuid
                                              )}
                                              onToggleSelect={handleToggleCreatorSelect}
                                              onEditTags={handleEditTags}
                                              onAddMobile={handleAddMobile}
                                          />
                                      ))}
                            </div>
                        </InfiniteScroll>
                    )}
                </div>
            </div>
            <CreatorFiltersModal
                isOpen={filtersModalOpen}
                onClose={() => setFiltersModalOpen(false)}
                currentFilters={baseFilters}
                onApply={(next) => setBaseFilters({ ...next, page: 1 })}
            />
            <BulkUploadCreatorContactModal
                isOpen={bulkUploadModalOpen}
                onClose={() => setBulkUploadModalOpen(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
            <AddCreatorContactModal
                isOpen={addCreatorModalOpen}
                onClose={() => setAddCreatorModalOpen(false)}
                onSuccess={handleAddCreatorSuccess}
            />
            <AddContactMobileModal
                isOpen={!!addMobileState}
                onClose={() => setAddMobileState(null)}
                socialAccountId={addMobileState?.socialAccountId}
                creatorName={addMobileState?.creatorName}
            />
            <HandleCreatorTagsModal
                isOpen={!!editTagsState}
                onClose={() => setEditTagsState(null)}
                socialAccountId={editTagsState?.socialAccountId}
                initialTags={editTagsState?.tags}
                creatorName={editTagsState?.creatorName}
            />
            <ManageCreatorTagsOffcanvas
                isOpen={manageTagsOpen}
                onClose={() => setManageTagsOpen(false)}
            />
        </>
    )
}

export default Creators
